// ── Color mode (light / dark) manager ────────────────────────────────
//
// Framework-free controller for the app's light/dark colour scheme.
// Pairs with the `[data-theme="dark"]` token override block in
// `assets/scss/_design-tokens.scss` — every chrome surface reads
// CSS custom properties (`--bg-page`, `--text-primary`, …) which
// flip when this module sets `data-theme="dark"` on `<html>`.
//
// Three user-selectable PREFERENCES:
//
//   "light"   force light (default)
//   "dark"    force dark
//   "system"  follow the OS `prefers-color-scheme`
//
// The PREFERENCE is what we persist to localStorage; the RESOLVED
// mode (`"light" | "dark"`) is what actually lands on the DOM. When
// the preference is "system" we resolve against `matchMedia` and
// re-resolve live whenever the OS scheme changes.
//
// This file is intentionally React-free so it can run before React
// mounts (see `initColorMode()` called from `index.js`), which is
// what avoids a flash of the wrong theme on first paint. The React
// binding lives in `helpers/useColorMode.js`.

export const COLOR_MODE_STORAGE_KEY = "riverr-color-mode";

export const COLOR_MODES = ["light", "dark", "system"];

const MEDIA_QUERY = "(prefers-color-scheme: dark)";

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const subscribers = new Set();
let mediaListenerBound = false;

/** Reads the persisted preference, defaulting to "light". */
export const getColorModePreference = () => {
  if (!isBrowser()) return "light";
  try {
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return COLOR_MODES.includes(stored) ? stored : "light";
  } catch {
    return "light";
  }
};

/** True when the OS currently prefers a dark scheme. */
const systemPrefersDark = () => {
  if (!isBrowser() || !window.matchMedia) return false;
  return window.matchMedia(MEDIA_QUERY).matches;
};

/** Resolves a preference to the concrete "light" | "dark" mode. */
export const resolveColorMode = (preference = getColorModePreference()) => {
  if (preference === "dark") return "dark";
  if (preference === "light") return "light";
  return systemPrefersDark() ? "dark" : "light";
};

/**
 * Writes the resolved mode to the DOM. Sets `data-theme` on the
 * document root (the selector the SCSS token overrides hook onto)
 * and `color-scheme` so native form controls / scrollbars follow.
 */
const applyResolvedMode = (resolved) => {
  if (!isBrowser()) return;
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  root.style.colorScheme = resolved;
};

const notify = () => {
  const snapshot = getColorModeState();
  subscribers.forEach((cb) => {
    try {
      cb(snapshot);
    } catch {
      /* never let one subscriber break the rest */
    }
  });
};

/** Current preference + resolved mode, for React snapshots. */
export const getColorModeState = () => {
  const preference = getColorModePreference();
  return { preference, mode: resolveColorMode(preference) };
};

// Bind a single OS-scheme listener that only matters while the
// preference is "system". Bound once; cheap no-op otherwise.
const ensureMediaListener = () => {
  if (mediaListenerBound || !isBrowser() || !window.matchMedia) return;
  const mql = window.matchMedia(MEDIA_QUERY);
  const handler = () => {
    if (getColorModePreference() === "system") {
      applyResolvedMode(resolveColorMode("system"));
      notify();
    }
  };
  // Safari <14 only supports the deprecated `addListener`.
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", handler);
  } else if (typeof mql.addListener === "function") {
    mql.addListener(handler);
  }
  mediaListenerBound = true;
};

/**
 * Persists a new preference and applies it immediately. Pass one of
 * "light" | "dark" | "system".
 */
export const setColorModePreference = (preference) => {
  const next = COLOR_MODES.includes(preference) ? preference : "light";
  if (isBrowser()) {
    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
    } catch {
      /* private mode / quota — still apply for this session */
    }
  }
  applyResolvedMode(resolveColorMode(next));
  notify();
};

/**
 * Convenience toggle between explicit light and dark. A "system"
 * preference resolves to its current concrete mode first, then
 * flips to the opposite explicit mode.
 */
export const toggleColorMode = () => {
  const current = resolveColorMode();
  setColorModePreference(current === "dark" ? "light" : "dark");
};

/**
 * Applies the stored preference to the DOM and wires the OS-scheme
 * listener. Call once, as early as possible (before React render in
 * `index.js`) to prevent a flash of the wrong theme.
 */
export const initColorMode = () => {
  if (!isBrowser()) return;
  applyResolvedMode(resolveColorMode());
  ensureMediaListener();
};

/** Subscribe to preference/mode changes. Returns an unsubscribe fn. */
export const subscribeColorMode = (callback) => {
  subscribers.add(callback);
  ensureMediaListener();
  return () => subscribers.delete(callback);
};
